import React, { useState, useEffect } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  BookOpenIcon, 
  AcademicCapIcon, 
  ShoppingBagIcon, 
  HeartIcon, 
  UserIcon,
  XMarkIcon,
  WrenchScrewdriverIcon,
  ArrowRightOnRectangleIcon,
  SparklesIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  HomeIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { useAuth } from '../../context/AuthContext';
import { supabase } from '../../lib/supabase';
import { isUserAdmin } from '../../config/admin';
import Logo from '../common/Logo';
import GandalfLogo from '../common/GandalfLogo';
import { playbooks } from '../../data/playbookTaxonomy';

const navigationItems = [
  { 
    name: 'Home', 
    path: '/', 
    icon: <HomeIcon className="w-5 h-5" />
  },
  { 
    name: 'Gandalf', 
    path: '/chat', 
    icon: <GandalfLogo size="icon" />,
    badge: 'New'
  },
  { 
    name: 'Playbook Library', 
    path: '/content', 
    icon: <BookOpenIcon className="w-5 h-5" />
  },
  { 
    name: 'Academy', 
    path: '/academy', 
    icon: <AcademicCapIcon className="w-5 h-5" />,
    comingSoon: true,
    badge: 'Coming Soon'
  },
  { 
    name: 'Marketplace', 
    path: '/marketplace', 
    icon: <ShoppingBagIcon className="w-5 h-5" />
  },
  { 
    name: 'My Collection', 
    path: '/collection', 
    icon: <HeartIcon className="w-5 h-5" />
  },
  {
    name: 'Open Org Toolkit',
    path: '/toolkit',
    icon: <WrenchScrewdriverIcon className="w-5 h-5" />
  }
];

export default function Sidebar({ closeSidebar, minimal = false, inPlaybookLayout = false }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [showAcademyTooltip, setShowAcademyTooltip] = useState(false);
  const [expandedPlaybooks, setExpandedPlaybooks] = useState({});
  const isAdmin = isUserAdmin(user);
  
  // Keep the outside click handler
  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (showAcademyTooltip) {
        // Check if the click is outside the tooltip and the academy button
        const tooltip = document.getElementById('academy-tooltip');
        const academyButton = document.getElementById('academy-button');
        
        if (tooltip && !tooltip.contains(event.target) && 
            academyButton && !academyButton.contains(event.target)) {
          setShowAcademyTooltip(false);
        }
      }
    };
    
    // Add event listener when tooltip is shown
    if (showAcademyTooltip) {
      document.addEventListener('mousedown', handleOutsideClick);
    }
    
    // Cleanup
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [showAcademyTooltip]);
  
  // Fetch user subscription data
  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!user) return;
      
      try {
        setIsLoading(true);
        const { data, error } = await supabase
          .from('profiles')
          .select('hasactivesubscription, subscriptionplan, subscriptionstartdate, subscription_cancelled_at_period_end')
          .eq('id', user.id)
          .single();
          
        if (error) {
          console.error('Error fetching subscription data:', error);
          return;
        }
        
        setSubscriptionData(data);
      } catch (error) {
        console.error('Error in subscription data fetch:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSubscriptionData();
  }, [user]);

  // Auto-expand active playbook when in playbook layout
  useEffect(() => {
    if (inPlaybookLayout) {
      const currentPlaybookId = getActivePlaybookId();
      if (currentPlaybookId) {
        setExpandedPlaybooks(prev => ({
          ...prev,
          [currentPlaybookId]: true
        }));
      }
    }
  }, [inPlaybookLayout, location.pathname]);
  
  // Format renewal date - 1 year from start date
  const getRenewalDate = () => {
    if (!subscriptionData?.subscriptionstartdate) return 'Unknown';
    
    const startDate = new Date(subscriptionData.subscriptionstartdate);
    const renewalDate = new Date(startDate);
    renewalDate.setFullYear(renewalDate.getFullYear() + 1);
    
    return renewalDate.toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const handleSignOut = async () => {
    const result = await signOut();
    if (result.success) {
      navigate('/signin');
    }
  };
  
  // Navigate to account page
  const goToAccount = () => {
    navigate('/account');
    if (closeSidebar) {
      closeSidebar();
    }
  };
  
  const togglePlaybook = (playbookId) => {
    setExpandedPlaybooks(prev => ({
      ...prev,
      [playbookId]: !prev[playbookId]
    }));
  };

  const isPlaybookActive = (playbookId) => {
    return location.pathname.startsWith(`/playbooks/${playbookId}`);
  };

  const isSectionActive = (playbookId, sectionId) => {
    return location.pathname === `/playbooks/${playbookId}/${sectionId}`;
  };

  // Get the active playbook ID from URL
  const getActivePlaybookId = () => {
    const match = location.pathname.match(/\/playbooks\/([^\/]+)/);
    return match ? match[1] : null;
  };

  // Filter playbooks to only show active one when in playbook layout
  const visiblePlaybooks = inPlaybookLayout 
    ? playbooks.filter(playbook => isPlaybookActive(playbook.id))
    : playbooks;

  return (
    <div className={`h-full flex flex-col min-h-screen bg-gray-900 ${minimal ? 'items-center w-16' : 'w-full'}`}>
      <div className={`flex items-center ${minimal ? 'justify-center px-0 pt-3 pb-2 w-full' : 'justify-start px-5 pt-4 pb-2'} w-full`}>
        <Logo minimal={minimal} size="small" />
      </div>
      <div className="border-t border-gray-800 w-full my-2" />
      {/* Primary Navigation - scrollable section */}
      <div className={`flex-1 overflow-y-auto ${minimal ? 'px-0 pt-2 pb-4 flex flex-col items-center' : 'px-3 py-4'} bg-gray-900`}>
        <div className={minimal ? 'flex flex-col gap-y-2.5 items-center w-full' : 'space-y-2 w-full'}>
          {navigationItems
            .filter(item => {
              // In playbook layout, only show content
              if (inPlaybookLayout) return item.path === '/content';
              
              // Show all items for regular users
              return true;
            })
            .map((item) => (
            <div key={item.path || item.name} className="relative w-full">
              {item.comingSoon ? (
                minimal ? (
                  <button
                    id="academy-button"
                    className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg hover:bg-gray-800 focus:bg-gray-800 transition-colors"
                    onClick={() => setShowAcademyTooltip(prev => !prev)}
                  >
                    <span className="flex justify-center w-full text-white">{React.cloneElement(item.icon, { className: 'w-5 h-5' })}</span>
                  </button>
                ) : (
                  <button
                    id="academy-button"
                    className="flex flex-row items-center space-x-3 w-full px-3 py-2 rounded-md text-gray-300 hover:bg-gray-800 hover:text-white transition-colors group text-sm"
                    onClick={() => setShowAcademyTooltip(prev => !prev)}
                  >
                    <span className="flex-shrink-0 text-white">{React.cloneElement(item.icon, { className: 'w-5 h-5' })}</span>
                    <span className="truncate font-medium text-sm">{item.name}</span>
                    {item.badge && (
                      <span className="ml-2 bg-primary/20 text-primary-light text-[10px] py-0 px-1 rounded">
                        {item.badge}
                      </span>
                    )}
                  </button>
                )
              ) : (
                <>
                  <NavLink
                    to={item.path}
                    end={item.path === '/'}
                    onClick={closeSidebar}
                    className={({ isActive }) => `
                      ${minimal ? 'flex items-center justify-center w-10 h-10 mx-auto rounded-lg text-xs' : 'flex flex-row items-center space-x-3 w-full px-3 py-2 rounded-md text-sm'}
                      transition-colors group
                      ${isActive 
                        ? 'bg-primary/10 text-primary' 
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
                      ${minimal ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}
                    `}
                  >
                    <span className="flex-shrink-0 text-white">{React.cloneElement(item.icon, { className: 'w-5 h-5' })}</span>
                    {!minimal && <span className="truncate font-medium text-sm">{item.name}</span>}
                    {!minimal && item.badge && (
                      <span className="ml-2 bg-primary/20 text-primary-light text-[10px] py-0 px-1 rounded">
                        {item.badge}
                      </span>
                    )}
                  </NavLink>
                </>
              )}
              {/* Fun Academy tooltip */}
              {!minimal && item.comingSoon && showAcademyTooltip && (
                <div 
                  id="academy-tooltip"
                  className="absolute left-0 top-full mt-2 w-full p-4 bg-gray-800 rounded-lg shadow-xl border border-gray-700 text-sm z-[9999]"
                  onClick={(e) => e.stopPropagation()}
                >
                  <div className="absolute top-0 left-6 transform -translate-y-1/2 rotate-45 w-2 h-2 bg-gray-800 border-t border-l border-gray-700"></div>
                  <div className="flex items-start mb-2">
                    <SparklesIcon className="w-5 h-5 text-primary-light mr-2 flex-shrink-0" />
                    <h4 className="font-medium text-small text-white">Academy Coming Soon!</h4>
                  </div>
                  <p className="text-gray-300 mb-3">We're curating the best cohorts and courses for progressive people. Watch this space!</p>
                  <div className="text-xs text-primary-light">
                    Have a recommendation? Email{' '}
                    <a href="mailto:adam@openorg.fyi" className="underline hover:text-white">
                      adam@openorg.fyi
                    </a>
                  </div>
                  <button 
                    className="absolute top-2 right-2 text-gray-400 hover:text-white"
                    onClick={() => setShowAcademyTooltip(false)}
                  >
                    <XMarkIcon className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Playbooks Section - Only show in playbook layout or not minimal */}
        {!minimal && inPlaybookLayout && (
          <>
            <div className="border-t border-gray-800 w-full my-6" />
            <h3 className="px-3 text-xs font-semibold text-gray-400 uppercase tracking-wider mb-4">
              {inPlaybookLayout ? 'CURRENT DOC' : 'Playbooks'}
            </h3>
            <div className="space-y-1">
              {visiblePlaybooks.map((playbook) => (
                <div key={playbook.id}>
                  <button
                    onClick={() => togglePlaybook(playbook.id)}
                    className={`w-full flex items-center justify-between px-3 py-2 rounded-md text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70
                      ${isPlaybookActive(playbook.id)
                        ? 'bg-primary/10 text-primary font-medium'
                        : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
                    `}
                  >
                    <span className="flex items-center flex-grow overflow-hidden mr-2">
                      <BookOpenIcon className="w-5 h-5 mr-2 flex-shrink-0" />
                      <span className="truncate max-w-full">{playbook.title}</span>
                    </span>
                    {expandedPlaybooks[playbook.id] ? (
                      <ChevronDownIcon className="w-4 h-4 flex-shrink-0" />
                    ) : (
                      <ChevronRightIcon className="w-4 h-4 flex-shrink-0" />
                    )}
                  </button>
                  {expandedPlaybooks[playbook.id] && (
                    <div className="ml-5 mt-1 space-y-1">
                      {playbook.sections.map((section) => (
                        <NavLink
                          key={section.id}
                          to={`/playbooks/${playbook.id}/${section.id}`}
                          onClick={closeSidebar}
                          className={({ isActive }) => `
                            block px-3 py-1.5 rounded-md text-sm transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-primary/70 overflow-hidden text-ellipsis whitespace-nowrap
                            ${isActive
                              ? 'bg-primary/10 text-primary font-medium'
                              : 'text-gray-400 hover:bg-gray-800 hover:text-white'}
                          `}
                          title={section.title}
                        >
                          {section.title}
                        </NavLink>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              
              {/* Add "All Content" link at the same level as current doc */}
              <NavLink
                to="/"
                className="flex items-center px-3 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-800 rounded-md transition-colors mt-4"
                onClick={closeSidebar}
              >
                <ChevronLeftIcon className="w-4 h-4 mr-2" />
                All Content
              </NavLink>
            </div>
          </>
        )}

        <div className="border-t border-gray-800 w-full my-6" />
      </div>
      {/* Need Help Section - fixed section near bottom */}
      {!minimal && !inPlaybookLayout && (
        <div className="flex-shrink-0 px-3 py-3 border-t border-gray-800 bg-gray-900">
          <div className="bg-gray-800/50 rounded-lg p-3 border border-gray-700/50">
            <h3 className="text-xs font-semibold text-gray-300 mb-2 flex items-center">
              <svg className="w-4 h-4 mr-1.5 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              Need help?
            </h3>
            
            <div className="space-y-2">
              <button
                onClick={() => {
                  if (window.Beacon) {
                    window.Beacon('open');
                  }
                }}
                className="flex items-center justify-center w-full py-2 px-3 bg-primary/10 hover:bg-primary/20 text-primary hover:text-primary-light rounded-md transition-colors border border-primary/20 hover:border-primary/30 group"
              >
                <svg className="w-4 h-4 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                </svg>
                <span className="text-xs font-medium">Chat with Adam & John</span>
              </button>
              <div className="flex items-center text-xs text-gray-400 justify-center">
                <span className="mr-1">or email</span>
                <a 
                  href="mailto:support@openorg.fyi" 
                  className="text-primary hover:text-primary-light transition-colors font-medium"
                >
                  support@openorg.fyi
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
      {minimal && (
        <div className="flex-shrink-0 px-0 py-2 border-t border-gray-800 bg-gray-900">
          <button
            title="Need help? Chat with founders via widget (bottom right) or email support@openorg.fyi"
            className="flex items-center justify-center w-10 h-10 mx-auto rounded-lg text-gray-300 hover:bg-gray-800 hover:text-primary transition-colors"
            onClick={() => {
              if (window.Beacon) {
                window.Beacon('open');
              }
            }}
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </button>
        </div>
      )}
      {/* Account Settings - fixed section at bottom */}
      <div className={`flex-shrink-0 ${minimal ? 'px-0 py-2 w-full' : 'px-3 py-4'} border-t border-gray-800 bg-gray-900`}>
        <NavLink
          to="/account"
          onClick={closeSidebar}
          className={({ isActive }) => `
            ${minimal ? 'flex items-center justify-center w-10 h-10 mx-auto my-1 rounded-lg text-xs' : 'flex flex-row items-center space-x-3 w-full px-3 py-2 rounded-md text-sm'}
            transition-colors group
            ${isActive 
              ? 'bg-primary/10 text-primary' 
              : 'text-gray-300 hover:bg-gray-800 hover:text-white'}
            ${minimal ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}
          `}
        >
          {({ isActive }) => (
            <>
              <span className={`flex-shrink-0 ${isActive ? 'text-primary' : 'text-white'}`}><UserIcon className="w-5 h-5" /></span>
              {!minimal && <span className="truncate font-medium text-sm">Account Settings</span>}
            </>
          )}
        </NavLink>
        <button
          onClick={handleSignOut}
          className={`w-full ${minimal ? 'flex items-center justify-center w-10 h-10 mx-auto my-1 rounded-lg text-xs' : 'flex flex-row items-center space-x-3 w-full px-3 py-2 rounded-md text-sm'} transition-colors group text-gray-300 hover:bg-gray-800 hover:text-white ${minimal ? 'hover:bg-gray-800 focus:bg-gray-800' : ''}`}
        >
          <span className="flex-shrink-0 text-white"><ArrowRightOnRectangleIcon className="w-5 h-5" /></span>
          {!minimal && <span className="truncate font-medium text-sm">Sign Out</span>}
        </button>
        {!minimal && !inPlaybookLayout && (
          <div 
            className="mt-4 card p-4 bg-gradient/5 relative overflow-hidden cursor-pointer hover:bg-gray-800/50 transition-colors rounded-lg mb-4"
            onClick={goToAccount}
          >
            <div className="absolute -right-4 -bottom-8 w-24 h-24 rounded-full bg-primary/10 blur-xl"></div>
            <div className="relative">
              <h3 className="text-sm font-medium text-white mb-2">Your Membership</h3>
              {isLoading ? (
                <p className="text-xs text-gray-300 mb-3">Loading...</p>
              ) : subscriptionData?.hasactivesubscription ? (
                <>
                  {subscriptionData?.subscription_cancelled_at_period_end ? (
                    <>
                      <p className="text-xs text-gray-300 mb-3">
                        Your subscription is cancelled but active until <span className="text-orange-400">{getRenewalDate()}</span>
                      </p>
                      <div className="flex items-center text-xs text-orange-300">
                        <span className="inline-block w-2 h-2 bg-orange-500 rounded-full mr-2"></span>
                        Cancelled (Active Until End)
                      </div>
                    </>
                  ) : (
                    <>
                      <p className="text-xs text-gray-300 mb-3">
                        Your subscription is active until <span className="text-primary">{getRenewalDate()}</span>
                      </p>
                      <div className="flex items-center text-xs text-gray-300">
                        <span className="inline-block w-2 h-2 bg-success rounded-full mr-2"></span>
                        Active
                      </div>
                    </>
                  )}
                </>
              ) : (
                <>
                  <p className="text-xs text-gray-300 mb-3">
                    No active subscription
                  </p>
                  <div className="flex items-center text-xs text-red-300">
                    <span className="inline-block w-2 h-2 bg-red-500 rounded-full mr-2"></span>
                    Inactive
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
} 